import React, { useState } from 'react';
import Breadcrumb from "app/components/Breadcrumb";
import { Delete, Edit, Search, Replay, PictureAsPdf, GetApp, Print, ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import useCollateralType from "./hooks/useCollateralType";
import CollateralTypeEditorDialog from "./components/CollateralTypeEditorDialog";
import ConfirmationDialog from "app/components/ConfirmationDialog";
import { themeShadows } from "app/components/MatxTheme/options/themeColors";
import {
    Box,
    Card,
    styled,
    TextField,
    InputAdornment,
    Button,
    IconButton,
    Tooltip,
    Switch,
    FormControlLabel,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import useRoles from "app/hooks/useRoles";
import useExport from "app/hooks/useExport";

const Status = styled("small")(({ theme, color }) => ({
    color: "#fff",
    padding: "2px 8px",
    overflow: "hidden",
    borderRadius: "300px",
    boxShadow: themeShadows[3],
    background: theme.palette[color]?.main || theme.palette.primary.main
}));

const exportColumns = [
    { label: 'Code', field: 'code' },
    { label: 'Description', field: 'description' },
    { label: 'Status', field: 'status' },
];

const Container = styled("div")(({ theme }) => ({
    margin: "30px",
    [theme.breakpoints.down("sm")]: { margin: "16px" },
    "& .breadcrumb": {
        marginBottom: "0px",
        [theme.breakpoints.down("sm")]: { marginBottom: "16px" }
    }
}));

const STATUSTYPES = {
    ACTIVE: 1,
    DEFAULT: 2,
    DELETED: 3,
};

export default function CollateralType() {
    const [newCollateralType, setNewCollateralType] = useState({
        fieldid: '',
        description: '',
        type: ''
    });

    const getrolebyusername = useRoles();
    const isActionEnabled = getrolebyusername.includes('SA') || getrolebyusername.includes('Admin');

    const handleAddNewUser = () => {
        setCollateralType(null);
        setShouldOpenEditorDialog(true);
    };

    const {
        handleEditUser,
        handleDeleteUser,
        shouldOpenEditorDialog,
        handleDialogClose,
        collateraltype,
        setShouldOpenEditorDialog,
        collateralTypes,
        setCollateralType,
        shouldOpenConfirmationDialog,
        handleConfirmationResponse,
        handleReactivate,
        handleReactivateConfirmationResponse,
        shouldOpenReactivateConfirmationDialog,
    } = useCollateralType();

    const [searchQuery, setSearchQuery] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleExportMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleExportMenuClose = () => setAnchorEl(null);

    const exportData = collateralTypes.map(ct => ({
        ...ct,
        status: ct.statusLUId === 1 ? 'Active' : ct.statusLUId === 2 ? 'Default' : 'Deleted',
    }));
    const { exportToPDF, exportToExcel, exportToCSV, handlePrint } = useExport(exportData, exportColumns, "CollateralType");

    const gridColumns = [
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'description', headerName: 'Description', flex: 2 },
        {
            field: 'statusLUId', headerName: 'Status', flex: 1,
            renderCell: (params) => {
                const val = params.value;
                if (val === STATUSTYPES.ACTIVE) return <Status color="success">Active</Status>;
                if (val === STATUSTYPES.DEFAULT) return <Status color="primary">Default</Status>;
                return <Status color="error">Deleted</Status>;
            }
        },
        ...(isActionEnabled ? [{
            field: 'actions', headerName: 'Action', flex: 1, sortable: false,
            renderCell: (params) => {
                const ct = params.row;
                if (ct.statusLUId === STATUSTYPES.DELETED) {
                    return (
                        <>
                            <Tooltip title="Edit">
                                <IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleEditUser(ct)(e); }}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reactivate">
                                <IconButton color="success" onClick={() => handleReactivate(ct)}>
                                    <Replay />
                                </IconButton>
                            </Tooltip>
                        </>
                    );
                }
                return (
                    <>
                        <IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleEditUser(ct)(e); }} disabled={ct.statusLUId === STATUSTYPES.DEFAULT}>
                            <Edit />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDeleteUser(ct)} disabled={ct.statusLUId === STATUSTYPES.DEFAULT}>
                            <Delete />
                        </IconButton>
                    </>
                );
            }
        }] : [])
    ];

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredCollateralTypeList = collateralTypes.filter(collateraltyperecord =>
        (showDeleted || collateraltyperecord.statusLUId !== STATUSTYPES.DELETED) &&
        (collateraltyperecord.code.toLowerCase().startsWith(String(searchQuery).toLowerCase()) ||
            collateraltyperecord.description.toLowerCase().startsWith(String(searchQuery).toLowerCase()))
    );

    const handleInputChange = (e) => {
        setNewCollateralType({ ...newCollateralType, [e.target.name]: e.target.value });
    };

    return (
        <Container>
            <div className="breadcrumb">
                <Breadcrumb routeSegments={[{ name: "Collateral Type" }]} />
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    sx={{ mb: 2, mt: 2 }}
                    color="primary"
                    variant="contained"
                    onClick={handleAddNewUser}
                >
                    Add Collateral Type
                </Button>

                <TextField
                    label="Search by Code or Description"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '300px' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        sx: { height: '40px' }
                    }}
                />
            </Box>
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={handlePrint}
                        startIcon={<Print />}
                    >
                        Print
                    </Button>
                    <Button
                        color="success"
                        variant="contained"
                        onClick={handleExportMenuOpen}
                        startIcon={<GetApp />}
                        endIcon={<ArrowDropDownIcon />}
                        sx={{ ml: 2 }}
                    >
                        Export
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleExportMenuClose}
                    >
                        <MenuItem onClick={() => { exportToPDF(); handleExportMenuClose(); }}>
                            <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                            <ListItemText primary="PDF" />
                        </MenuItem>
                        <MenuItem onClick={() => { exportToExcel(); handleExportMenuClose(); }}>
                            <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Excel" />
                        </MenuItem>
                        <MenuItem onClick={() => { exportToCSV(); handleExportMenuClose(); }}>
                            <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                            <ListItemText primary="CSV" />
                        </MenuItem>
                    </Menu>
                </Box>
                <FormControlLabel
                    control={<Switch checked={showDeleted} onChange={() => setShowDeleted(prev => !prev)} />}
                    label="Show Deleted"
                />
            </Box>
            <Card sx={{ width: "100%", overflow: "auto" }} elevation={6}>
                <DataGrid
                    rows={filteredCollateralTypeList}
                    columns={gridColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    autoHeight
                    disableSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        },
                    }}
                />
            </Card>

            {
                shouldOpenEditorDialog && (
                    <CollateralTypeEditorDialog
                        open={shouldOpenEditorDialog}
                        handleClose={handleDialogClose}
                        collateraltype={collateraltype}
                    />
                )
            }

            {
                shouldOpenConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to delete this Collateral Type?"
                    />
                )
            }

            {
                shouldOpenReactivateConfirmationDialog && (
                    <ConfirmationDialog
                        open={shouldOpenReactivateConfirmationDialog}
                        onConfirmDialogClose={handleDialogClose}
                        onYesClick={handleReactivateConfirmationResponse}
                        title="Are you sure?"
                        text="Are you sure you want to reactivate this Collateral Type?"
                    />
                )
            }
        </Container >

    );
}